/**
 * Clean Perspective Warping System 
 */

// Gaussian elimination solver
function solveLinearSystem(A, b) {
  const n = A.length;
  const augmented = A.map((row, i) => [...row, b[i]]);

  // Forward elimination
  for (let i = 0; i < n; i++) {
    // Find pivot
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
        maxRow = k;
      }
    }

    // Swap rows
    [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

    // Check for singular matrix
    if (Math.abs(augmented[i][i]) < 1e-10) {
      return null;
    }

    // Eliminate column
    for (let k = i + 1; k < n; k++) {
      const factor = augmented[k][i] / augmented[i][i];
      for (let j = i; j < n + 1; j++) {
        augmented[k][j] -= factor * augmented[i][j];
      }
    }
  }

  // Back substitution
  const x = new Array(n);
  for (let i = n - 1; i >= 0; i--) {
    x[i] = augmented[i][n] / augmented[i][i];
    for (let k = i - 1; k >= 0; k--) {
      augmented[k][n] -= augmented[k][i] * x[i];
    }
  }

  return x;
}

// Calculate the homography matrix from source points to destination points
function calculateHomography(srcPoints, dstPoints) {
  if (srcPoints.length !== 4 || dstPoints.length !== 4) {
    throw new Error('Both source and destination must have exactly 4 points');
  }

  const A = [];
  const b = [];

  for (let i = 0; i < 4; i++) {
    const sx = srcPoints[i].x;
    const sy = srcPoints[i].y;
    const dx = dstPoints[i].x;
    const dy = dstPoints[i].y;

    A.push([sx, sy, 1, 0, 0, 0, -sx * dx, -sy * dx]);
    b.push(dx);
    A.push([0, 0, 0, sx, sy, 1, -sx * dy, -sy * dy]);
    b.push(dy);
  }

  const h = solveLinearSystem(A, b);
  
  if (!h) {
    // console.error('Failed to calculate homography matrix');
    return null;
  }

  return [
    [h[0], h[1], h[2]],
    [h[3], h[4], h[5]], 
    [h[6], h[7], 1]
  ];
}

// Transform a point using the homography matrix
function transformPoint(point, homographyMatrix) {
  const H = homographyMatrix;
  const x = point.x;
  const y = point.y;

  const w = H[2][0] * x + H[2][1] * y + H[2][2];
  
  if (Math.abs(w) < 1e-10) {
    return { x: 0, y: 0 };
  }

  const transformedX = (H[0][0] * x + H[0][1] * y + H[0][2]) / w;
  const transformedY = (H[1][0] * x + H[1][1] * y + H[1][2]) / w;

  return { x: transformedX, y: transformedY };
}

// Main perspective warp function that preserves full canvas dimensions
export function applyPerspectiveWarp({ ctx, img, from, to }) {
  try {
    // console.log('🔧  applyPerspectiveWarp called');
    // console.log('Canvas dimensions before warp:', ctx.canvas.width, 'x', ctx.canvas.height);
    
    // Calculate homography from destination back to source (inverse mapping)
    const homography = calculateHomography(to, from);
    
    if (!homography) {
      console.error('Could not calculate homography, falling back to simple draw');
      ctx.drawImage(img, to[0].x, to[0].y, 200, 150);
      return;
    }

    // CRITICAL FIX: Use the FULL CANVAS dimensions, not the bounding box
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    
    // console.log('Using FULL canvas dimensions:', canvasWidth, 'x', canvasHeight);

    // Get bounding box of destination quadrilateral (for optimization only)
    const minX = Math.max(0, Math.min(...to.map(p => p.x)));
    const maxX = Math.min(canvasWidth, Math.max(...to.map(p => p.x)));
    const minY = Math.max(0, Math.min(...to.map(p => p.y)));
    const maxY = Math.min(canvasHeight, Math.max(...to.map(p => p.y)));

    // Create temporary canvas at 2x resolution for better anti-aliasing
    // BUT preserve the aspect ratio and relative positioning
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    const scale = 2; // Render at 2x size then scale down
    
    // : Create temp canvas for the full processing area, not just bounding box
    tempCanvas.width = Math.ceil((maxX - minX) * scale);
    tempCanvas.height = Math.ceil((maxY - minY) * scale);

    // console.log('Temp canvas size:', tempCanvas.width, 'x', tempCanvas.height);
    // console.log('Processing region:', minX, minY, 'to', maxX, maxY);

    // Enable high-quality rendering
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';

    // Get source image data
    const sourceCanvas = document.createElement('canvas');
    const sourceCtx = sourceCanvas.getContext('2d');
    sourceCanvas.width = img.naturalWidth || img.width;
    sourceCanvas.height = img.naturalHeight || img.height;
    sourceCtx.imageSmoothingEnabled = true;
    sourceCtx.imageSmoothingQuality = 'high';
    sourceCtx.drawImage(img, 0, 0);
    const sourceImageData = sourceCtx.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
    const sourceData = sourceImageData.data;

    // Create output image data at 2x resolution
    const outputImageData = tempCtx.createImageData(tempCanvas.width, tempCanvas.height);
    const outputData = outputImageData.data;

    // Point-in-polygon test for quadrilateral
    function isPointInQuad(x, y, quad) {
      let inside = false;
      for (let i = 0, j = quad.length - 1; i < quad.length; j = i++) {
        if (((quad[i].y > y) !== (quad[j].y > y)) &&
            (x < (quad[j].x - quad[i].x) * (y - quad[i].y) / (quad[j].y - quad[i].y) + quad[i].x)) {
          inside = !inside;
        }
      }
      return inside;
    }

    // Simple but high-quality bilinear interpolation
    function sampleImage(x, y) {
      const x0 = Math.floor(x);
      const y0 = Math.floor(y);
      const x1 = x0 + 1;
      const y1 = y0 + 1;

      if (x0 < 0 || y0 < 0 || x1 >= sourceCanvas.width || y1 >= sourceCanvas.height) {
        return [0, 0, 0, 0];
      }

      const fx = x - x0;
      const fy = y - y0;

      const getPixel = (px, py) => {
        const idx = (py * sourceCanvas.width + px) * 4;
        return [
          sourceData[idx],     // R
          sourceData[idx + 1], // G  
          sourceData[idx + 2], // B
          sourceData[idx + 3]  // A
        ];
      };

      const p00 = getPixel(x0, y0);
      const p10 = getPixel(x1, y0);
      const p01 = getPixel(x0, y1);
      const p11 = getPixel(x1, y1);

      return [
        p00[0] * (1 - fx) * (1 - fy) + p10[0] * fx * (1 - fy) + p01[0] * (1 - fx) * fy + p11[0] * fx * fy,
        p00[1] * (1 - fx) * (1 - fy) + p10[1] * fx * (1 - fy) + p01[1] * (1 - fx) * fy + p11[1] * fx * fy,
        p00[2] * (1 - fx) * (1 - fy) + p10[2] * fx * (1 - fy) + p01[2] * (1 - fx) * fy + p11[2] * fx * fy,
        p00[3] * (1 - fx) * (1 - fy) + p10[3] * fx * (1 - fy) + p01[3] * (1 - fx) * fy + p11[3] * fx * fy
      ];
    }

    // console.log('Rendering at', scale, 'x resolution for anti-aliasing');

    // Apply inverse mapping at 2x resolution (only process the relevant region)
    for (let y = 0; y < tempCanvas.height; y++) {
      for (let x = 0; x < tempCanvas.width; x++) {
        // Convert back to original coordinates
        const destX = (x / scale) + minX;
        const destY = (y / scale) + minY;

        // Check if point is inside the destination quadrilateral
        if (isPointInQuad(destX, destY, to)) {
          // Map destination point back to source using homography
          const sourcePoint = transformPoint({ x: destX, y: destY }, homography);
          
          // Sample the source image
          const [r, g, b, a] = sampleImage(sourcePoint.x, sourcePoint.y);
          
          const outputIndex = (y * tempCanvas.width + x) * 4;
          outputData[outputIndex] = Math.round(r);
          outputData[outputIndex + 1] = Math.round(g);
          outputData[outputIndex + 2] = Math.round(b);
          outputData[outputIndex + 3] = Math.round(a);
        }
      }
    }

    // Put the warped image data to temporary canvas
    tempCtx.putImageData(outputImageData, 0, 0);

    // CRITICAL: Draw the result back to the ORIGINAL canvas at correct position
    // This preserves the full canvas dimensions
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      tempCanvas, 
      0, 0, tempCanvas.width, tempCanvas.height,
      minX, minY, (maxX - minX), (maxY - minY)
    );

    // console.log('Canvas-level anti-aliasing applied');
    // console.log('Canvas dimensions after warp:', ctx.canvas.width, 'x', ctx.canvas.height);
    // console.log('✅  applyPerspectiveWarp completed - canvas size preserved');

  } catch (error) {
    // console.error('Perspective warp failed:', error);/
    ctx.drawImage(img, to[0].x, to[0].y, 200, 150);
  }
}

// Generate CSS transform matrix for live preview
export function generateCSSTransform(srcPoints, dstPoints) {
  try {
    const homography = calculateHomography(srcPoints, dstPoints);
    
    if (!homography) {
      return 'matrix3d(1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1)';
    }

    const H = homography;
    
    // Convert 3x3 homography to 4x4 matrix for CSS
    return `matrix3d(${H[0][0]}, ${H[1][0]}, 0, ${H[2][0]}, ${H[0][1]}, ${H[1][1]}, 0, ${H[2][1]}, 0, 0, 1, 0, ${H[0][2]}, ${H[1][2]}, 0, ${H[2][2]})`;
    
  } catch (error) {
    console.error('CSS transform generation failed:', error);
    return 'matrix3d(1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1)';
  }
}

// Export alias for mobile compatibility
export { applyPerspectiveWarp as applyPerspectiveTransform };